import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import HomePage from "./pages/HomePage";
import EventsFeedPage from "./pages/EventsFeedPage";
import CreateEventPage from "./pages/CreateEventPage";
import EventPage from "./pages/EventPage";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import OnboardingPage from "./pages/OnboardingPage";
import ProfilePage from "./pages/ProfilePage";
import MyEventsPage from "./pages/MyEventsPage";
import HostProfilePage from "./pages/HostProfilePage";
import FAQPage from "./pages/FAQPage";
import SupportPage from "./pages/SupportPage";
import ForOrganizersPage from "./pages/ForOrganizersPage";
import AdminPage from "./pages/AdminPage";
import CheckInExperiencePage from "./pages/CheckInExperiencePage";
import DevGuestBar from "./components/DevGuestBar";
import SiteFooter from "./components/SiteFooter";
import { isDemoWorkflowPath } from "./lib/demoRoutes";
import { DemoChooserProvider } from "./lib/demoChooserContext";
import { AuthProvider } from "./lib/auth";
import { GuestIdentityProvider } from "./lib/guestIdentity";
import { DevSessionProvider } from "./lib/devSession";
import { LocaleProvider } from "./lib/locale";
import { HostThemePreviewProvider } from "./lib/hostThemePreview";
import { RESERVED_CITY_SLUGS } from "./lib/eventLinks";
import NotFoundPage from "./pages/NotFoundPage";
import "./games/favoriteDances";
import FavoriteDancesHowItWorksPage from "./pages/FavoriteDancesHowItWorksPage";
import LegacyEventRedirect from "./pages/LegacyEventRedirect";
import PushBootstrap from "./components/notifications/PushBootstrap";
import SchoolPage from "./pages/SchoolPage";
import ClassPage from "./pages/ClassPage";
import ClassDashboardPage from "./pages/ClassDashboardPage";
import SchoolPeoplePage from "./pages/SchoolPeoplePage";
import {
  PublicEventListingDrawer,
  PublicClassListingDrawer,
} from "./components/profile/PublicListingDrawer";
import { listingDrawerBackground } from "./lib/listingDrawer";

const DemoEventPage = lazy(() => import("./pages/DemoEventPage"));
const DemoCheckInPage = lazy(() => import("./pages/DemoCheckInPage"));
const DemoVotePage = lazy(() => import("./pages/DemoVotePage"));
const DemoHostDashboardPage = lazy(() => import("./pages/DemoHostDashboardPage"));
const DemoClassPage = lazy(() => import("./pages/DemoClassPage"));
const DemoClassDashboardPage = lazy(() => import("./pages/DemoClassDashboardPage"));
const DemoSchoolPage = lazy(() => import("./pages/DemoSchoolPage"));
const SwitchUserPage = lazy(() => import("./pages/SwitchUserPage"));

/** Single quiet product language — Home is the source of truth. */
function themeForPath() {
  return "quiet";
}

/**
 * Sets `data-theme="quiet"` on <html>.
 * Marketing night heroes use local `.home-hero` CSS, not a separate theme.
 */
function RouteThemeSync() {
  const loc = useLocation();
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", themeForPath(loc.pathname));
  }, [loc.pathname]);
  return null;
}

/** Scroll window to top on route change — skip when opening a listing drawer overlay. */
function ScrollToTop() {
  const location = useLocation();
  const hasListingDrawer = Boolean(listingDrawerBackground(location));
  useEffect(() => {
    if (hasListingDrawer) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search, hasListingDrawer]);
  return null;
}

function FriendlyEventRoute() {
  const { citySlug } = useParams();
  if (!citySlug || RESERVED_CITY_SLUGS.has(citySlug.toLowerCase())) {
    return <Navigate to="/" replace />;
  }
  return <EventPage />;
}

function LegacySchoolRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/school/${slug}`} replace />;
}

function LegacySchoolPeopleRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/school/${slug}/people`} replace />;
}

function AppShell() {
  const loc = useLocation();
  const background = listingDrawerBackground(loc);
  const displayPath = (background || loc).pathname;
  const footerVariant = displayPath === "/" ? "home" : "default";
  const demoWorkflow = isDemoWorkflowPath(displayPath);
  const hideSiteFooter = demoWorkflow || displayPath === "/onboarding";

  return (
    <DemoChooserProvider>
    <div className="min-h-screen flex flex-col">
      <RouteThemeSync />
      <ScrollToTop />
      <PushBootstrap />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            border: "1px solid var(--border-subtle)",
            boxShadow: "var(--shadow-lift)",
            borderRadius: 12,
            background: "var(--cream)",
            color: "var(--ink)",
            fontFamily: "Inter, system-ui, sans-serif",
            fontWeight: 500,
          },
        }}
      />
      <main className="flex-1">
        <Suspense fallback={null}>
          <Routes location={background || loc}>
            <Route path="/" element={<HomePage />} />
            <Route path="/events" element={<EventsFeedPage />} />
            <Route path="/host/:organizerId" element={<HostProfilePage />} />
            <Route path="/school/:slug/people" element={<SchoolPeoplePage />} />
            <Route path="/school/:schoolSlug/class/:classSlug" element={<ClassPage />} />
            <Route path="/school/:slug" element={<SchoolPage />} />
            <Route path="/schools/:slug/people" element={<LegacySchoolPeopleRedirect />} />
            <Route path="/schools/:slug" element={<LegacySchoolRedirect />} />
            <Route path="/classes/:classId/dashboard" element={<ClassDashboardPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/for-organizers" element={<ForOrganizersPage />} />
            <Route path="/admin/:slug" element={<SchoolPage />} />
            <Route path="/admin" element={<Navigate to="/app-admin" replace />} />
            <Route path="/app-admin" element={<AdminPage />} />
            <Route path="/switch-user" element={<SwitchUserPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/me" element={<ProfilePage />} />
            <Route path="/me/host" element={<Navigate to="/me?tab=host" replace />} />
            <Route path="/my-events" element={<MyEventsPage />} />
            <Route path="/create" element={<CreateEventPage />} />
            <Route path="/e/:eventId/edit" element={<CreateEventPage />} />
            <Route path="/favorite-dances" element={<FavoriteDancesHowItWorksPage />} />
            <Route path="/e/:eventId" element={<LegacyEventRedirect />} />
            <Route path="/:citySlug/:eventSlug/check-in" element={<CheckInExperiencePage />} />
            <Route path="/demo/check-in/board" element={<DemoCheckInPage />} />
            <Route path="/demo/check-in" element={<DemoCheckInPage />} />
            <Route path="/demo/vote" element={<DemoVotePage />} />
            <Route path="/demo/host" element={<DemoHostDashboardPage />} />
            <Route path="/demo/class/dashboard" element={<DemoClassDashboardPage />} />
            <Route path="/demo/class" element={<DemoClassPage />} />
            <Route path="/demo/school" element={<DemoSchoolPage />} />
            <Route path="/demo" element={<DemoEventPage />} />
            <Route path="/dashboard/:eventId" element={<DashboardPage />} />
            <Route path="/:citySlug/:eventSlug" element={<FriendlyEventRoute />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          {background ? (
            <Routes>
              <Route path="/school/:schoolSlug/class/:classSlug" element={<PublicClassListingDrawer />} />
              <Route path="/:citySlug/:eventSlug" element={<PublicEventListingDrawer />} />
            </Routes>
          ) : null}
        </Suspense>
      </main>
      {!hideSiteFooter && <SiteFooter variant={footerVariant} />}
      <DevGuestBar />
    </div>
    </DemoChooserProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <GuestIdentityProvider>
      <DevSessionProvider>
      <LocaleProvider>
      <HostThemePreviewProvider>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
      </HostThemePreviewProvider>
      </LocaleProvider>
      </DevSessionProvider>
      </GuestIdentityProvider>
    </AuthProvider>
  );
}
